import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { apiClient } from "@/api/client";
import { formatMediumDate } from "@/utils/date";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface AccessRequest {
  request_id: string;
  firm_name: string;
  contact_name: string;
  email: string;
  state: string;
  conflict_flag: string;
  submitted_at: string;
  status: string;
}

interface AccessRequestsResponse {
  requests: AccessRequest[];
  count: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  return formatMediumDate(iso);
}

function ConflictBadge({ flag }: { flag: string }) {
  if (flag === "texas_rofr") {
    return (
      <Badge className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50">
        TX ROFR
      </Badge>
    );
  }
  if (flag === "state_conflict_review") {
    return (
      <Badge className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50">
        Review
      </Badge>
    );
  }
  return null;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function PendingRequestsPanel() {
  const { data: requestsResponse, loading, execute } = useApi<AccessRequestsResponse>();

  const fetchRequests = useCallback(() => {
    execute(() =>
      apiClient.get<AccessRequestsResponse>("/v1/admin/access-requests")
        .then(r => r.data)
        .catch(() => ({ requests: [], count: 0 }) as AccessRequestsResponse)
    );
  }, [execute]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (requestId: string, firmName: string) => {
    try {
      await apiClient.post(`/v1/admin/access-requests/${requestId}/approve`);
      toast.success(`${firmName} approved`);
      fetchRequests();
    } catch {
      toast.error("Approval flow not yet implemented");
    }
  };

  const handleDecline = async (requestId: string, firmName: string) => {
    try {
      await apiClient.post(`/v1/admin/access-requests/${requestId}/reject`);
      toast.success(`${firmName} declined`);
      fetchRequests();
    } catch {
      toast.error("Decline flow not yet implemented");
    }
  };

  const pendingList = (requestsResponse?.requests ?? []).filter(r => r.status === "pending");

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (pendingList.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No pending access requests.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Firm Name</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Conflict Flag</TableHead>
          <TableHead>Submitted</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pendingList.map(req => (
          <TableRow key={req.request_id}>
            <TableCell className="font-medium">{req.firm_name}</TableCell>
            <TableCell>{req.contact_name}</TableCell>
            <TableCell className="text-muted-foreground">{req.email}</TableCell>
            <TableCell>{req.state}</TableCell>
            <TableCell>
              <ConflictBadge flag={req.conflict_flag} />
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {fmtDate(req.submitted_at)}
            </TableCell>
            <TableCell>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onClick={() => handleApprove(req.request_id, req.firm_name)}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => handleDecline(req.request_id, req.firm_name)}
                >
                  Decline
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default PendingRequestsPanel;
